import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { apiFetch, friendlyErrorMessage } from 'markcat-api-client';
import styles from './Login.module.css';
import { usePageTitle } from '../hooks/usePageTitle';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  usePageTitle('Reset password — Markcat');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const errorId = 'reset-password-error';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    setLoading(true);
    try {
      await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, new_password: password }),
      });
      setSuccess(true);
    } catch (err: unknown) {
      setError(friendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h2 className={styles.title}>Invalid link</h2>
          <p className={styles.subtitle}>This password reset link is invalid or has expired.</p>
          <p className={styles.footer}>
            <a href="/login">Back to sign in</a>
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h2 className={styles.title}>Password reset</h2>
          <p className={styles.subtitle}>Your password has been reset. You can now sign in.</p>
          <button className={styles.button} onClick={() => navigate('/login')} style={{ width: '100%' }}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h2 className={styles.title}>Set new password</h2>
        {error && <p id={errorId} className={styles.error} role="alert">{error}</p>}
        <form className={styles.form} onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder="New password"
            className={styles.input}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
          />
          <input
            type="password"
            placeholder="Confirm password"
            className={styles.input}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
          />
          <button type="submit" className={styles.button} disabled={loading}>
            {loading ? 'Resetting...' : 'Reset password'}
          </button>
        </form>
        <p className={styles.footer}>
          <a href="/login">Back to sign in</a>
        </p>
      </div>
    </div>
  );
}
